import { useEffect, useLayoutEffect, useRef, type RefObject } from 'react';

import { useTranslation } from '@nucleus-commerce/i18n/react';
import { cn } from '@nucleus-commerce/ui/lib/utils';

import { segmentDisplay, type TemporalLocale } from './locale';
import { scrollWithin } from './scroll';
import { type SegmentOptions, type SegmentType, type SegmentValues } from './segments';
import { columnOptions, type TimeColumnType } from './time-range';

const isTimeColumn = (type: SegmentType): type is TimeColumnType =>
  type === 'hour' || type === 'minute' || type === 'period';

export interface TimeColumnsProps {
  locale: TemporalLocale;
  options: SegmentOptions;
  values: SegmentValues;
  isDisabled: (type: TimeColumnType, option: number) => boolean;
  /** An option chosen by click, Space or an arrow key; the popup stays open. */
  onPick: (type: TimeColumnType, option: number) => void;
  /** Enter: done. */
  onEnter: () => void;
  /** Filled with a function that focuses the hour column (the time picker's initial focus). */
  focusRef?: RefObject<(() => void) | null>;
  className?: string;
}

/**
 * Chrome's time columns: hours, minutes (by the step) and AM/PM in the locale's order, each a
 * listbox scrolled so its selected option sits at the top. Up/Down move and set the value,
 * Left/Right switch columns, Home/End jump to the ends, Enter closes.
 */
export function TimeColumns({
  locale,
  options,
  values,
  isDisabled,
  onPick,
  onEnter,
  focusRef,
  className,
}: TimeColumnsProps) {
  const { t } = useTranslation('common');
  const rootRef = useRef<HTMLDivElement>(null);
  const columns = locale.layouts.time.flatMap((item) =>
    item.kind === 'segment' && isTimeColumn(item.type) ? [item.type] : [],
  );
  const pendingFocus = useRef<{ type: TimeColumnType; option: number } | null>(null);

  const column = (type: TimeColumnType) =>
    rootRef.current?.querySelector<HTMLElement>(`[data-column="${type}"]`);
  const optionElement = (type: TimeColumnType, option: number) =>
    column(type)?.querySelector<HTMLElement>(`[data-option="${option}"]`);
  const tabStop = (type: TimeColumnType) =>
    column(type)?.querySelector<HTMLElement>('[role="option"][tabindex="0"]');

  // On open every column shows its selected option at the top.
  useLayoutEffect(() => {
    for (const type of columns) {
      const selected = column(type)?.querySelector<HTMLElement>('[aria-selected="true"]');
      if (selected) scrollWithin(column(type), selected, 'start');
    }
    // Only on mount; later moves scroll the least needed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!focusRef) return;
    focusRef.current = () => {
      const first = columns[0];
      if (first) tabStop(first)?.focus({ preventScroll: true });
    };
  });

  // Keyboard moves keep the focused option in view once the new value has rendered.
  useEffect(() => {
    const target = pendingFocus.current;
    if (!target) return;
    pendingFocus.current = null;
    const element = optionElement(target.type, target.option);
    scrollWithin(column(target.type), element, 'nearest');
    element?.focus({ preventScroll: true });
  });

  const enabledOptions = (type: TimeColumnType) =>
    columnOptions(type, options).filter((option) => !isDisabled(type, option));

  const onKeyDown = (type: TimeColumnType, option: number) => (event: React.KeyboardEvent) => {
    const enabled = enabledOptions(type);
    const index = enabled.indexOf(option);
    const pick = (next: number | undefined) => {
      if (next === undefined) return;
      pendingFocus.current = { type, option: next };
      onPick(type, next);
    };
    switch (event.key) {
      case 'ArrowUp':
        event.preventDefault();
        // Columns wrap around, as Chrome's looping columns do.
        pick(enabled[index <= 0 ? enabled.length - 1 : index - 1]);
        return;
      case 'ArrowDown':
        event.preventDefault();
        pick(enabled[index === -1 || index >= enabled.length - 1 ? 0 : index + 1]);
        return;
      case 'Home':
        event.preventDefault();
        pick(enabled[0]);
        return;
      case 'End':
        event.preventDefault();
        pick(enabled.at(-1));
        return;
      case 'ArrowLeft':
      case 'ArrowRight': {
        event.preventDefault();
        const next = columns[columns.indexOf(type) + (event.key === 'ArrowLeft' ? -1 : 1)];
        if (next) tabStop(next)?.focus({ preventScroll: true });
        return;
      }
      case 'Enter':
        event.preventDefault();
        onEnter();
        return;
      case ' ':
        event.preventDefault();
        if (!isDisabled(type, option)) pick(option);
        return;
    }
  };

  return (
    <div ref={rootRef} data-slot="time-columns" className={cn('flex h-full', className)}>
      {columns.map((type, columnIndex) => {
        const all = columnOptions(type, options);
        const selectedValue = values[type];
        const stop = all.includes(selectedValue ?? NaN)
          ? selectedValue
          : all.find((option) => !isDisabled(type, option));
        return (
          <div
            key={type}
            role="listbox"
            aria-label={t(`dateTime.column.${type}`)}
            data-column={type}
            className={cn(
              'flex h-full w-14 flex-col gap-px overflow-y-auto p-1 [scrollbar-width:none]',
              columnIndex > 0 && 'border-l',
            )}
          >
            {all.map((option) => {
              const selected = option === selectedValue;
              const disabled = isDisabled(type, option);
              return (
                <div
                  key={option}
                  role="option"
                  aria-selected={selected}
                  aria-disabled={disabled || undefined}
                  tabIndex={option === stop ? 0 : -1}
                  data-option={option}
                  className={cn(
                    'flex h-8 shrink-0 cursor-default items-center justify-center rounded-md text-sm tabular-nums transition-colors outline-none select-none',
                    'hover:bg-accent hover:text-accent-foreground',
                    'focus-visible:ring-ring/50 focus-visible:ring-[3px]',
                    'aria-disabled:pointer-events-none aria-disabled:opacity-40',
                    selected &&
                      'bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground',
                  )}
                  onClick={(event) => {
                    if (disabled) return;
                    event.currentTarget.focus({ preventScroll: true });
                    onPick(type, option);
                  }}
                  onKeyDown={onKeyDown(type, option)}
                >
                  {segmentDisplay(type, option, locale)}
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}
